import * as Sentry from '@sentry/react';

// Only production reports: the staging backend sends to another Sentry org, so its traces could not be
// linked anyway. To try it locally, set REACT_APP_SENTRY_ENVIRONMENT in .env.local.
const environment =
  window.location.hostname === 'app.totalhipaa.com'
    ? 'production'
    : process.env.REACT_APP_SENTRY_ENVIRONMENT;
const dsn = process.env.REACT_APP_SENTRY_DSN;

// Reset/welcome links contain credentials. Keep paths useful without sending URL queries or hashes.
const withoutUrlParameters = (url: string): string => url.split(/[?#]/, 1)[0];

const sanitizeEventUrl = <T extends Sentry.Event>(event: T): T => {
  if (event.request?.url) {
    event.request.url = withoutUrlParameters(event.request.url);
  }
  const headers = event.request?.headers;
  if (headers) {
    Object.keys(headers).forEach((header) => {
      if (/^referer$/i.test(header)) {
        headers[header] = withoutUrlParameters(headers[header]);
      }
    });
  }
  return event;
};

if (dsn && environment) {
  Sentry.init({
    dsn,
    environment,
    release: process.env.REACT_APP_VERSION,
    beforeSend: sanitizeEventUrl,
    beforeSendTransaction: sanitizeEventUrl,
    beforeBreadcrumb: (breadcrumb) => {
      const data = breadcrumb.data;
      if (data) {
        ['url', 'from', 'to'].forEach((key) => {
          if (typeof data[key] === 'string') {
            data[key] = withoutUrlParameters(data[key]);
          }
        });
      }
      return breadcrumb;
    },
    integrations: [
      Sentry.browserTracingIntegration({
        // Numeric ids become :id, so /companies/12 and /companies/34 are the same transaction.
        beforeStartSpan: (options) => ({
          ...options,
          name: options.name.replace(/\/\d+(?=\/|$)/g, '/:id'),
          attributes: {
            ...options.attributes,
            [Sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: 'route',
          },
        }),
      }),
    ],
    tracesSampleRate: environment === 'production' ? 0.2 : 1.0,
    // sentry-trace/baggage go only to our API, which continues the trace on the backend.
    tracePropagationTargets: [process.env.REACT_APP_API_URL as string],
    // Benign browser noise, triggered by the UI library's resize observers.
    ignoreErrors: [/ResizeObserver loop/],
  });
}
